'use client';

import React, { useState, useEffect, useMemo } from 'react';
import ProductsHeader from '@/components/dashboard/products/ProductsHeader';
import ProductsFilterBar from '@/components/dashboard/products/ProductsFilterBar';
import ProductsGroupTabs from '@/components/dashboard/products/ProductsGroupTabs';
import ProductsCategoryList, { ProductItem } from '@/components/dashboard/products/ProductsCategoryList';
import ProductFormView from '@/components/dashboard/products/ProductFormView';
import StockEntryModal from '@/components/dashboard/products/StockEntryModal';

export default function ProductsPage() {
  const [viewMode, setViewMode] = useState<'LIST' | 'FORM'>('LIST');
  const [editingProduct, setEditingProduct] = useState<ProductItem | null>(null);

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [selectedBrand, setSelectedBrand] = useState('ALL');
  const [selectedStockFilter, setSelectedStockFilter] = useState('ALL');
  const [selectedStatus, setSelectedStatus] = useState('ALL');
  const [selectedSort, setSelectedSort] = useState('NAME_ASC');
  const [groupBy, setGroupBy] = useState<'CATEGORY' | 'BRAND'>('CATEGORY');

  // Datos
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [stockEntryOpen, setStockEntryOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 4000);
  };

  // Cargar productos desde PostgreSQL
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/products');
      const data = await res.json();
      if (data.success) {
        setProducts(data.products || []);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Extraer categorías y marcas únicas
  const categories = useMemo(() => {
    const set = new Set<string>();
    products.forEach(p => {
      if (p.category) set.add(p.category);
    });
    return Array.from(set);
  }, [products]);

  const brands = useMemo(() => {
    const set = new Set<string>();
    products.forEach(p => {
      if (p.brand) set.add(p.brand);
    });
    return Array.from(set);
  }, [products]);

  // Filtrado y ordenamiento de productos
  const filteredProducts = useMemo(() => {
    return products
      .filter((p) => {
        // Buscador
        if (searchTerm.trim()) {
          const q = searchTerm.toLowerCase();
          const matchName = p.name.toLowerCase().includes(q);
          const matchBrand = p.brand?.toLowerCase().includes(q);
          const matchSku = p.sku?.toLowerCase().includes(q);
          const matchBarcode = p.barcode?.includes(q);
          const matchPrice = p.salePrice.toString().includes(q);
          if (!matchName && !matchBrand && !matchSku && !matchBarcode && !matchPrice) {
            return false;
          }
        }

        // Categoría
        if (selectedCategory !== 'ALL' && p.category !== selectedCategory) {
          return false;
        }

        // Marca
        if (selectedBrand !== 'ALL' && p.brand !== selectedBrand) {
          return false;
        }

        // Stock
        if (selectedStockFilter === 'IN_STOCK' && p.stock <= 0) return false;
        if (selectedStockFilter === 'OUT_OF_STOCK' && p.stock > 0) return false;
        if (selectedStockFilter === 'LOW_STOCK' && (p.stock > p.minStock || p.stock <= 0)) return false;

        // Estado
        if (selectedStatus === 'ACTIVE' && !p.active) return false;
        if (selectedStatus === 'INACTIVE' && p.active) return false;

        return true;
      })
      .sort((a, b) => {
        if (selectedSort === 'NAME_ASC') return a.name.localeCompare(b.name);
        if (selectedSort === 'PRICE_DESC') return b.salePrice - a.salePrice;
        if (selectedSort === 'PRICE_ASC') return a.salePrice - b.salePrice;
        if (selectedSort === 'STOCK_ASC') return a.stock - b.stock;
        if (selectedSort === 'STOCK_DESC') return b.stock - a.stock;
        return 0;
      });
  }, [products, searchTerm, selectedCategory, selectedBrand, selectedStockFilter, selectedStatus, selectedSort]);

  // Guardar producto (Nuevo o Editar)
  const handleSaveProduct = async (formData: any) => {
    const url = '/api/products';
    const method = formData.id ? 'PUT' : 'POST';

    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    });

    const data = await res.json();
    if (!data.success) {
      throw new Error(data.error || 'Error al guardar el producto');
    }

    showToast(formData.id ? '¡Producto actualizado correctamente!' : '¡Producto creado y añadido al catálogo!');
    fetchProducts();
    setViewMode('LIST');
    setEditingProduct(null);
  };

  // Ajuste rápido de stock (+1 / -1)
  const handleQuickAdjustStock = async (product: ProductItem, delta: number) => {
    const newStock = Math.max(0, product.stock + delta);
    try {
      const res = await fetch('/api/products', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: product.id,
          stock: newStock
        })
      });
      const data = await res.json();
      if (data.success) {
        setProducts(products.map(p => p.id === product.id ? { ...p, stock: newStock } : p));
      }
    } catch (err) {
      console.error('Error ajustando stock:', err);
    }
  };

  // Recepción de stock por modal
  const handleSubmitStockEntry = async (payload: {
    productId: string;
    quantity: number;
    costPrice?: number;
    notes?: string;
  }) => {
    const res = await fetch('/api/products/stock-entry', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const data = await res.json();
    if (!data.success) {
      throw new Error(data.error || 'No se pudo registrar el ingreso de stock');
    }

    showToast(`¡Se ingresaron ${payload.quantity} unidades físicas al stock!`);
    fetchProducts();
  };

  return (
    <div className="space-y-4">
      {/* Toast flotante */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 bg-slate-900 text-white px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-3 text-sm font-semibold animate-in slide-in-from-bottom-5 duration-200">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
          <span>{toastMessage}</span>
        </div>
      )}

      {viewMode === 'FORM' ? (
        /* Vista de Alta / Edición estructurada en 6 tarjetas */
        <ProductFormView
          initialProduct={editingProduct}
          categories={categories}
          onBack={() => {
            setViewMode('LIST');
            setEditingProduct(null);
          }}
          onSave={handleSaveProduct}
        />
      ) : (
        /* Vista Principal: Listado y Catálogo */
        <>
          {/* 1. Cabecera con Título, Conteo y Accesos Rápidos */}
          <ProductsHeader productsCount={filteredProducts.length} />

          {/* 2. Barra de Herramientas, Filtros y Botones Principales */}
          <ProductsFilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            categories={categories}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            brands={brands}
            selectedBrand={selectedBrand}
            onBrandChange={setSelectedBrand}
            selectedStockFilter={selectedStockFilter}
            onStockFilterChange={setSelectedStockFilter}
            selectedStatus={selectedStatus}
            onStatusChange={setSelectedStatus}
            selectedSort={selectedSort}
            onSortChange={setSelectedSort}
            onOpenStockEntry={() => setStockEntryOpen(true)}
            onOpenNewProduct={() => {
              setEditingProduct(null);
              setViewMode('FORM');
            }}
            onOpenImportMenu={() => alert('Opción de importación masiva de Excel disponible en configuraciones.')}
          />

          {/* 3. Selector de Agrupación [Por categoría | Por marca] */}
          <ProductsGroupTabs
            groupBy={groupBy}
            onGroupByChange={setGroupBy}
            onViewAll={() => {
              setSelectedCategory('ALL');
              setSelectedBrand('ALL');
              setSearchTerm('');
            }}
          />

          {/* 4. Lista Agrupada por Acordeones y Tarjetas de Producto */}
          <ProductsCategoryList
            products={filteredProducts}
            groupBy={groupBy}
            loading={loading}
            onEditProduct={(p) => {
              setEditingProduct(p);
              setViewMode('FORM');
            }}
            onOpenNewProduct={() => {
              setEditingProduct(null);
              setViewMode('FORM');
            }}
            onQuickAdjustStock={handleQuickAdjustStock}
          />

          {/* MODAL: Ingreso de Stock */}
          <StockEntryModal
            isOpen={stockEntryOpen}
            onClose={() => setStockEntryOpen(false)}
            products={products}
            onSubmitStockEntry={handleSubmitStockEntry}
          />
        </>
      )}
    </div>
  );
}
